import React, { useEffect, useRef, useState } from "react";

import {
  ArrowLeftIcon as ArrowLeft,
  ArrowRightIcon as ArrowRight,
  InfoIcon as Info,
  InfoSmIcon,
  SuccessIcon,
  Typography,
  WingIcon as Wing
} from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { useSelector, useDispatch } from "react-redux";

import FormattedMessageWithConfigValues from "Components/FormattedMessageWithConfigValues";
import { APP_NAME, CLI_COMMAND_NAME } from "Constants/documentationUrls";
import { CloseButton, IconControl, Link } from "ds/Button";
import * as Code from "ds/Code";
import Dialog from "ds/Dialog";
import * as Dropdown from "ds/Dropdown";
import * as Tabs from "ds/Tabs";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  deploymentSelectors,
  loadingNextDeploymentSelector,
  loadingCurrentDeploymentSelector
} from "Reducers/environment/deployment";
import {
  selectFeatureProjectSetupWizardEnabled,
  selectFeatureProjectSetupTipsEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { organizationSelector } from "Reducers/organization";
import { gitProjectSelector } from "Reducers/project/project";
import {
  loadRegistry,
  nextWizardStep,
  prevWizardStep,
  toggleProjectWizard,
  projectWizardSelector,
  configDataSelector,
  goToWizardStep
} from "Reducers/project/wizard";
import { subscriptionSelector } from "Reducers/subscription";
import Intro from "src/organization/pages/project/components/Wizard/Intro";
import Outro from "src/organization/pages/project/components/Wizard/Outro";
import Step from "src/organization/pages/project/components/Wizard/Step";
import { useAppSelector } from "Store/hooks";

import * as S from "./ContentWizard.styles";

const ContentSetupTips = ({ environmentId }) => {
  const intl = useIntl();
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const subscription = useSelectorWithUrlParams(subscriptionSelector, {
    id: project?.getSubscriptionId()
  });
  const organization = useAppSelector(state =>
    organizationSelector(state, {
      organizationId: project?.organization_id
    })
  );
  const isDemoProject = subscription?.project_options?.initialize?.profile
    ?.toLowerCase()
    .includes("demo");

  const {
    currentDeployment: selectCurrentDeployment,
    nextDeployment: selectNextDeployment
  } = deploymentSelectors;

  const isNextDeploymentLoading = useAppSelector(loadingNextDeploymentSelector);
  const isCurrentDeploymentLoading = useAppSelector(
    loadingCurrentDeploymentSelector
  );
  const [isCurrentDeploymentLoaded, setIsCurrentDeploymentLoaded] =
    useState(false);
  const [isNextDeploymentLoaded, setIsNextDeploymentLoaded] = useState(false);

  const currentDeployment = useAppSelector(state =>
    selectCurrentDeployment(state, {
      organizationId: organization?.name,
      projectId: project?.id,
      environmentId: environmentId ?? project?.default_branch
    })
  );

  const nextDeployment = useAppSelector(state =>
    selectNextDeployment(state, {
      organizationId: organization?.name,
      projectId: project?.id,
      environmentId: environmentId ?? project?.default_branch
    })
  );
  const deployment = currentDeployment || nextDeployment;

  useEffect(() => {
    if (isCurrentDeploymentLoaded) return;
    if (currentDeployment || isCurrentDeploymentLoading === false)
      setIsCurrentDeploymentLoaded(true);
  }, [isCurrentDeploymentLoading, currentDeployment]);

  useEffect(() => {
    if (isNextDeploymentLoaded) return;
    if (nextDeployment || isNextDeploymentLoading === false)
      setIsNextDeploymentLoaded(true);
  }, [isNextDeploymentLoading, nextDeployment]);

  const areDeploymentsLoaded =
    isNextDeploymentLoaded && isCurrentDeploymentLoaded;

  if (!areDeploymentsLoaded || !!deployment) {
    return null;
  }

  return (
    <S.SetupWrapper>
      <S.SetupTipsWrapper>
        {isDemoProject ? (
          <>
            <S.InfoWrapper>
              <S.IconWrapper aria-label="info-trigger-demo-project">
                <InfoSmIcon color="var(--icon-color-secondary-500-fill,var(--color-secondary-500))" />
              </S.IconWrapper>
              <S.EbonyBodyText size="medium" weight="semiBold">
                <FormattedMessageWithConfigValues id="project.setup.tips.info" />
              </S.EbonyBodyText>
            </S.InfoWrapper>
            <S.EbonyLightBodyText size="medium">
              <FormattedMessageWithConfigValues id="project.setup.tips.description" />
            </S.EbonyLightBodyText>
            <S.CodeInstallLabelContainer>
              <S.StepLabel>
                <Typography.BodyText size="small" weight="semiBold">
                  {intl.formatMessage({
                    id: "onboarding.setup.install"
                  })}
                </Typography.BodyText>
                <S.RequiredDotIcon />
              </S.StepLabel>
              <Dialog
                description={intl.formatMessage(
                  {
                    id: "onboarding.setup.install.dialog"
                  },
                  { app_name: APP_NAME }
                )}
                analyticId="onboarding.setup.install.dialog"
                trigger={
                  <IconControl>
                    <Info color="var(--mode-vector-neutral-default)" />
                  </IconControl>
                }
                link={
                  <Link
                    analyticId="learnmore"
                    href={intl.formatMessage({
                      id: "links.documentation.CLI"
                    })}
                  >
                    {intl.formatMessage({
                      id: "learnmore"
                    })}
                  </Link>
                }
              />
            </S.CodeInstallLabelContainer>
            <Tabs.Root defaultValue="homebrew">
              <S.TabsList>
                <Tabs.Trigger value="homebrew">
                  {intl.formatMessage({
                    id: "onboarding.setup.brew"
                  })}
                </Tabs.Trigger>
                <Tabs.Trigger value="shell">
                  {intl.formatMessage({
                    id: "onboarding.setup.shell"
                  })}
                </Tabs.Trigger>
                <Tabs.Trigger value="scoop">
                  {intl.formatMessage({
                    id: "onboarding.setup.scoop"
                  })}
                </Tabs.Trigger>
              </S.TabsList>
              <Tabs.Content value="homebrew">
                <S.CopyWrapper>
                  <Code.Block isCopyable id="project.setup.cli.install.brew">
                    {intl.formatMessage({
                      id: "project.setup.cli.install.brew"
                    })}
                  </Code.Block>
                </S.CopyWrapper>
              </Tabs.Content>
              <Tabs.Content value="shell">
                <S.CopyWrapper>
                  <Code.Block isCopyable id="project.setup.cli.install.shell">
                    {intl.formatMessage(
                      {
                        id: "project.setup.cli.install.shell"
                      },
                      { cli_command: CLI_COMMAND_NAME }
                    )}
                  </Code.Block>
                </S.CopyWrapper>
              </Tabs.Content>
              <Tabs.Content value="scoop">
                <S.CopyWrapper>
                  <Code.Block isCopyable id="project.setup.cli.install.scoop">
                    {intl.formatMessage({
                      id: "project.setup.cli.install.scoop"
                    })}
                  </Code.Block>
                </S.CopyWrapper>
              </Tabs.Content>
            </Tabs.Root>
            <S.CopyWrapper>
              <S.CopyLabel>
                <FormattedMessage id="project.setup.tips.clone" />
              </S.CopyLabel>
              <Code.Block isCopyable id="project.setup.tips.clone_command">
                {intl.formatMessage({
                  id: "project.setup.tips.clone_command"
                })}
              </Code.Block>
            </S.CopyWrapper>
            <S.CopyWrapper>
              <S.CopyLabel>
                <FormattedMessage
                  id="project.setup.tips.connect_repo"
                  values={{
                    app_name: intl.formatMessage({ id: "app_name" })
                  }}
                />
              </S.CopyLabel>
              <Code.Block
                isCopyable
                id="project.setup.tips.connect_repo.command"
              >
                {intl.formatMessage(
                  {
                    id: "project.setup.tips.connect_repo.command"
                  },
                  {
                    cli_command: CLI_COMMAND_NAME,
                    projectId: project?.id
                  }
                )}
              </Code.Block>
            </S.CopyWrapper>
            <S.CopyWrapper>
              <S.CopyLabel>
                <FormattedMessage
                  id="project.setup.tips.deploy"
                  values={{
                    app_name: intl.formatMessage({ id: "app_name" })
                  }}
                />
              </S.CopyLabel>
              <Code.Block
                isCopyable
                id="project.setup.tips.deploy.demo_command"
              >
                {intl.formatMessage(
                  {
                    id: "project.setup.tips.deploy.demo_command"
                  },
                  {
                    cli_command: CLI_COMMAND_NAME
                  }
                )}
              </Code.Block>
            </S.CopyWrapper>
            <S.CopyWrapper>
              <S.CopyLabel>
                <FormattedMessage id="project.setup.tips.launch" />
              </S.CopyLabel>
              <Code.Block isCopyable id="project.setup.tips.launch_command">
                {intl.formatMessage(
                  {
                    id: "project.setup.tips.launch_command"
                  },
                  {
                    cli_command: CLI_COMMAND_NAME
                  }
                )}
              </Code.Block>
            </S.CopyWrapper>
          </>
        ) : (
          <>
            <S.InfoWrapper>
              <S.IconWrapper aria-label="info-trigger-wizard-tips">
                <InfoSmIcon color="var(--icon-color-secondary-500-fill,var(--color-secondary-500))" />
              </S.IconWrapper>
              <S.EbonyBodyText size="medium" weight="semiBold">
                <FormattedMessageWithConfigValues id="project.setup.tips.push" />
              </S.EbonyBodyText>
            </S.InfoWrapper>
            <S.EbonyLightBodyText size="medium">
              <FormattedMessageWithConfigValues id="project.setup.tips.steps" />
            </S.EbonyLightBodyText>
            <S.CodeInstallLabelContainer>
              <S.StepLabel>
                <Typography.BodyText size="small" weight="semiBold">
                  {intl.formatMessage({
                    id: "onboarding.setup.install"
                  })}
                </Typography.BodyText>
                <S.RequiredDotIcon />
              </S.StepLabel>
              <Dialog
                analyticId="onboarding.setup.install.dialog"
                description={intl.formatMessage(
                  {
                    id: "onboarding.setup.install.dialog"
                  },
                  { app_name: APP_NAME }
                )}
                trigger={
                  <IconControl>
                    <Info />
                  </IconControl>
                }
                link={
                  <Link
                    analyticId="learnmore"
                    href={intl.formatMessage({
                      id: "links.documentation.CLI"
                    })}
                  >
                    {intl.formatMessage({
                      id: "learnmore"
                    })}
                  </Link>
                }
              />
            </S.CodeInstallLabelContainer>

            <Tabs.Root defaultValue="homebrew">
              <S.TabsList>
                <Tabs.Trigger value="homebrew">
                  {intl.formatMessage({
                    id: "onboarding.setup.brew"
                  })}
                </Tabs.Trigger>
                <Tabs.Trigger value="shell">
                  {intl.formatMessage({
                    id: "onboarding.setup.shell"
                  })}
                </Tabs.Trigger>
                <Tabs.Trigger value="scoop">
                  {intl.formatMessage({
                    id: "onboarding.setup.scoop"
                  })}
                </Tabs.Trigger>
              </S.TabsList>

              <Tabs.Content value="homebrew">
                <S.CopyWrapper>
                  <Code.Block isCopyable id="project.setup.cli.install.brew">
                    {intl.formatMessage({
                      id: "project.setup.cli.install.brew"
                    })}
                  </Code.Block>
                </S.CopyWrapper>
              </Tabs.Content>
              <Tabs.Content value="shell">
                <S.CopyWrapper>
                  <Code.Block isCopyable id="project.setup.cli.install.shell">
                    {intl.formatMessage(
                      {
                        id: "project.setup.cli.install.shell"
                      },
                      { cli_command: CLI_COMMAND_NAME }
                    )}
                  </Code.Block>
                </S.CopyWrapper>
              </Tabs.Content>
              <Tabs.Content value="scoop">
                <S.CopyWrapper>
                  <Code.Block isCopyable id="project.setup.cli.install.scoop">
                    {intl.formatMessage({
                      id: "project.setup.cli.install.scoop"
                    })}
                  </Code.Block>
                </S.CopyWrapper>
              </Tabs.Content>
            </Tabs.Root>

            <S.CopyWrapper>
              <S.CopyLabel>
                <FormattedMessageWithConfigValues id="project.setup.tips.connect" />
              </S.CopyLabel>
              <Code.Block isCopyable id="project.setup.tips.connect_command">
                {intl.formatMessage(
                  {
                    id: "project.setup.tips.connect_command"
                  },
                  {
                    cli_command: CLI_COMMAND_NAME,
                    projectId: project?.id
                  }
                )}
              </Code.Block>
            </S.CopyWrapper>
            <S.CopyWrapper>
              <S.CopyLabel>
                <FormattedMessageWithConfigValues id="project.setup.tips.deploy" />
              </S.CopyLabel>
              <Code.Block isCopyable id="project.setup.tips.deploy_commands">
                {intl.formatMessage(
                  {
                    id: "project.setup.tips.deploy_commands"
                  },
                  {
                    app_name: APP_NAME,
                    cli_command: CLI_COMMAND_NAME
                  }
                )}
              </Code.Block>
            </S.CopyWrapper>

            <S.LinkButton
              content="mixed"
              href={intl.formatMessage({
                id: "links.documentation.configure_project"
              })}
              analyticId="project.setup.tips.configurations"
            >
              <FormattedMessage id="project.setup.tips.configurations" />
              <Wing />
            </S.LinkButton>
          </>
        )}
      </S.SetupTipsWrapper>
    </S.SetupWrapper>
  );
};

const ContentWizard = ({ project, user, subscription }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const containerRef = useRef();

  const { organizationId, projectId } = useDecodedParams();

  const projectWizard = useSelector(state =>
    projectWizardSelector(state, { organizationId, projectId })
  );
  const configData = useSelector(configDataSelector);

  useEffect(() => {
    if (projectWizard?.isOpen && !configData) dispatch(loadRegistry());
  }, [projectWizard?.isOpen]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.setAttribute("tabindex", 0);
    container.focus();
    container.removeAttribute("tabindex");
  }, [projectWizard?.currentStep]);

  if (!projectWizard?.data || !projectWizard?.isOpen || projectWizard?.loading)
    return null;

  const { currentStep, finish, start, data: steps, completed } = projectWizard;

  const prevStep = e => {
    e?.preventDefault();
    dispatch(prevWizardStep({ organizationId, projectId }));
  };

  const nextStep = e => {
    e?.preventDefault();
    dispatch(nextWizardStep({ organizationId, projectId }));
  };

  const handleClose = e => {
    e?.preventDefault();
    dispatch(toggleProjectWizard({ organizationId, projectId }));
  };

  const goToStep = step => {
    dispatch(goToWizardStep({ organizationId, projectId, step }));
  };

  const progress = currentStep && steps && (currentStep / steps.length) * 100;

  return (
    <S.Wrapper
      className={`wizard$${!start && !finish ? " step-wizard" : ""}`}
      animate={projectWizard?.animate}
      ref={containerRef}
    >
      {start && <Intro project={project} />}
      {finish && <Outro project={project} />}

      {!start && !finish && (
        <>
          <S.ProgressBar completed={progress} />

          <S.Header>
            <Dropdown.Root>
              <S.DropdownTrigger variant="mixed">
                {intl.formatMessage(
                  { id: "wizard.steps.dropdown" },
                  {
                    current: currentStep,
                    total: steps.length
                  }
                )}
                <Dropdown.Chevron />
              </S.DropdownTrigger>
              <Dropdown.Portal>
                <Dropdown.Content>
                  {steps?.map((step, index) => (
                    <Dropdown.Item
                      $isSelected={index + 1 === currentStep}
                      key={step.id}
                      onSelect={() => {
                        goToStep(index + 1);
                      }}
                      analyticId={`wizard.steps.dropdown.${step.id}`}
                    >
                      {`${index + 1}. ${step.label}`}
                      {!step.required && (
                        <S.DropdownLabel>
                          {intl.formatMessage({
                            id: "wizard.list.steps.optional",
                            defaultMessage: "(Opt)"
                          })}
                        </S.DropdownLabel>
                      )}
                      {completed?.[step.id] && (
                        <SuccessIcon color="var(--theme-success-600)" />
                      )}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
            <CloseButton onClick={handleClose} />
          </S.Header>

          <Step
            subscription={subscription}
            project={project}
            step={steps[currentStep - 1]}
            user={user}
          />

          <S.Footer>
            <S.BackBtn variant="link" onClick={prevStep} analyticId="back-link">
              <ArrowLeft />
            </S.BackBtn>
            <S.Pipe />
            <S.ForwardBtn
              variant="link"
              analyticId={
                steps[currentStep]
                  ? steps[currentStep].label
                  : intl.formatMessage({ id: "wizard.finish" })
              }
              onClick={nextStep}
            >
              {steps[currentStep]
                ? steps[currentStep].label
                : intl.formatMessage({ id: "wizard.finish" })}
              <ArrowRight />
            </S.ForwardBtn>
          </S.Footer>
        </>
      )}
    </S.Wrapper>
  );
};

const ContentWizardHOC = props => {
  const projectSetupWizardEnabled = useAppSelector(
    selectFeatureProjectSetupWizardEnabled
  );
  const projectSetupTipsEnabled = useAppSelector(
    selectFeatureProjectSetupTipsEnabled
  );

  return projectSetupTipsEnabled ? (
    <ContentSetupTips environmentId={props.environmentId} />
  ) : projectSetupWizardEnabled ? (
    <ContentWizard {...props} />
  ) : null;
};

const propsTypes = {
  project: PropTypes.object,
  user: PropTypes.object,
  subscription: PropTypes.object,
  environmentId: PropTypes.string
};

ContentWizard.propTypes = propsTypes;
ContentWizardHOC.propTypes = propsTypes;
ContentSetupTips.propTypes = propsTypes;

export default ContentWizardHOC;
